import type { FC } from "react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

import Typography from "@/design-system/Typography";
import Carousel from "@/design-system/carousel";
import { FetchGet } from "@/lib/fetch-helper";
import { sendTelemetryEvent } from "@/lib/telemetry";
import CategoryIcon from "@/src/common/category-icon";
import { useViewportInfoContext } from "@/src/contexts";
import { useQueryCustomNoSuspense } from "@/src/query-client-overrides";
import { browserStatics } from "@/src/shared/BrowserStatic";
import { TelemetryEntity, urlFormat } from "@Components/Modules/Helpers";
import { Cashback } from "@Routes/CashbackLinks";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, colors } from "./../../styles/tokens.stylex";
import type { ICashbackCategoryData } from "./CashbackCategoryData";
import cashbackCategoryData from "./CashbackCategoryData";

export interface ICashbackCategory {
    code: string;
    id: string;
}

export interface ICashbackCategories {
    allCategory?: boolean;
    initialCategoryId: string;
    onChangeCategory: (categoryKey: string, categoryCode: string) => void;
    initialData?: ICashbackCategory[];
}

const CashbackCategories: FC<ICashbackCategories> = memo(
    (props) => {
        const { isMobile } = useViewportInfoContext();
        const [categoryId, setCategoryId] = useState(props.initialCategoryId);
        const { language } = browserStatics;
        const categoryList = cashbackCategoryData(language, props.allCategory || false);
        const { data } = useQueryCustomNoSuspense({
            queryKey: ["cashbackCategories"],
            queryFn: () =>
                FetchGet<ICashbackCategory[]>(urlFormat(Cashback, { action: "GetCashbackCategories" })),
            initialData: props.initialData,
        });

        useEffect(() => {
            if (props.initialCategoryId !== categoryId) {
                setCategoryId(props.initialCategoryId);
            }
            // categoryId is intentionally omitted to avoid infinite loop
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [props.initialCategoryId]);

        const onCategoryChange = useCallback(
            (code: string, categoryKey: string) => {
                setCategoryId(categoryKey);
                props.onChangeCategory(categoryKey, code);
                sendTelemetryEvent({
                    eventName: `${TelemetryEntity.Cashback}-${TelemetryEntity.Category}`,
                    actionType: "Click",
                    itemId: categoryKey,
                });
            },
            [props],
        );

        const categoryButtons = useMemo(() => {
            const categories =
                data?.length > 0
                    ? [
                          {
                              code: props.allCategory ? "ALL" : "POPULAR",
                              id: "00000000-0000-0000-0000-000000000000",
                          },
                          ...data,
                      ]
                    : [];

            return categoryList.map((cat: ICashbackCategoryData, index: number) => {
                if (!categories.length) {
                    return (
                        <button key={index} {...stylex.props(styles.categoryListItem)} type="button">
                            <CategoryIcon categoryType={cat.code} />
                            <Typography textElement="span" fontStyle="BodyM" styleX={styles.categoryName}>
                                {cat.name}
                            </Typography>
                        </button>
                    );
                }
                const categoryItem = categories.find((x: ICashbackCategory) => x.code === cat.code);
                if (!categoryItem) {
                    return null;
                }

                return (
                    <button
                        key={categoryItem.id}
                        onClick={() => onCategoryChange(categoryItem.code, categoryItem.id)}
                        {...stylex.props(
                            styles.categoryListItem,
                            categoryId === categoryItem.id && styles.active,
                        )}
                        type="button"
                    >
                        <CategoryIcon categoryType={categoryItem.code} />
                        <Typography textElement="span" fontStyle="BodyM" styleX={styles.categoryName}>
                            {cat.name}
                        </Typography>
                    </button>
                );
            });
        }, [categoryList, data, categoryId, onCategoryChange, props.allCategory]);

        const renderCategoriesContent = () => {
            if (isMobile) {
                return (
                    <Carousel showArrows={false} styleX={styles.carouselOlContainer} fullWidth>
                        {categoryButtons}
                    </Carousel>
                );
            }

            return <div {...stylex.props(styles.wrapperCategories)}>{categoryButtons}</div>;
        };

        return <div {...stylex.props(styles.carouselWrapper)}>{renderCategoriesContent()}</div>;
    },
    (prevProps, nextProps) => prevProps.initialCategoryId === nextProps.initialCategoryId,
);

const styles = stylex.create({
    wrapperCategories: {
        columnGap: ComposableSpacing.s3,
        display: "flex",
        flexWrap: "wrap",
        rowGap: ComposableSpacing.s2,
        width: "100%",
    },
    categoryListItem: {
        borderColor: { default: colors.b_centas, ":hover": "rgba(223, 225, 229, 0)" },
        borderRadius: 25,
        borderStyle: "solid",
        borderWidth: 2,
        gap: ComposableSpacing.s2,
        paddingBlock: "8px",
        paddingInline: "14px",
        alignItems: "center",
        backgroundColor: "#f3f4f8",
        boxShadow: { default: null, ":hover": "0 1px 6px 0 rgba(32, 33, 36, 0.28)" },
        color: colors.grayLight,
        display: "flex",
        fontSize: "1rem",
        userSelect: "none",
    },

    carouselOlContainer: {
        gap: ComposableSpacing.s2,
        gridAutoColumns: "auto",
        paddingBottom: ComposableSpacing.s2,
        paddingTop: ComposableSpacing.s2,
    },

    active: {
        borderColor: colors.b_BrandMagenta,
        borderStyle: "solid",
        borderWidth: 2,
        backgroundColor: "#fff",
        color: colors.fg_BrandMagenta,
    },

    categoryName: {
        color: "inherit",
        fontWeight: "bold",
        textTransform: "none",
        textWrap: "nowrap",
    },

    carouselWrapper: {
        borderBottomColor: colors.b_centas,
        borderBottomStyle: "solid",
        borderBottomWidth: 1,
        paddingBottom: 15,
    },
});

export default CashbackCategories;
